// The only way this app reaches its data.
//
// Every call is same-origin (/api/** is a Hosting rewrite to the `api`
// function, and the dev server proxies it), and every dashboard call carries a
// Firebase ID token in an Authorization header. There is no cookie and no
// credentialed request: the token is the whole of the client's identity, which
// is why a leaked one expires in an hour and why nothing here ever stores it.

import { firebaseAuth } from "./firebase";
import { fileExtension, fileTooBigMessage } from "./knowledge";
import type { PaidPlanId } from "./plans";
import type {
  BillingSummary,
  TeamMember,
  TeamView,
  BookingSettings,
  BookingStatus,
  BookingTarget,
  BookingTargetOption,
  CreateAgentInput,
  CustomApiAuthInput,
  CustomApiCall,
  CustomApiResult,
  CustomApiStatus,
  HandoffKind,
  HandoffStatus,
  DashboardAgent,
  KnowledgeSource,
  Me,
  QaPair,
  SessionFilters,
  SessionRow,
  SignedUpload,
  RemovedKnowledgeSource,
  StoredKnowledgeSource,
  LiveAvailability,
  Transcript,
  UpdateAgentInput,
  AdminOverview,
  AdminList
} from "./types";

export const API_BASE = "/api";

export class ApiError extends Error {
  status: number;
  // The server sends per-source messages as `errors` on a 400 from the agent
  // wizard. Carrying the whole body means a caller can show all of them at once
  // instead of the one-line summary.
  data: Record<string, unknown>;

  constructor(message: string, status: number, data: Record<string, unknown> = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }

  get fieldErrors(): string[] {
    const errors = this.data.errors;
    return Array.isArray(errors) ? errors.map(String) : [];
  }
}

type RequestOptions = {
  method?: string;
  body?: unknown;
  // Off only for calls made before sign-in; everything in the dashboard needs it.
  authenticated?: boolean;
  signal?: AbortSignal;
};

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, authenticated = true, signal } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";

  if (authenticated) {
    const user = firebaseAuth().currentUser;
    if (!user) throw new ApiError("You are signed out.", 401);
    // Not cached. getIdToken() returns the cached token until it is close to
    // expiring and refreshes it transparently, so caching it here would only
    // add a way to send a stale one.
    headers.Authorization = `Bearer ${await user.getIdToken()}`;
  }

  let response: Response;
  try {
    response = await fetch(API_BASE + path, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      // No cookies, ever. The ID token is the only credential.
      credentials: "omit",
      signal
    });
  } catch (err) {
    if ((err as Error)?.name === "AbortError") throw err;
    // fetch rejects for DNS, offline and CORS alike, none of which have a
    // status. 0 keeps callers from having to special-case `undefined`.
    throw new ApiError("Could not reach the server. Check your connection and try again.", 0);
  }

  const payload = await readJson(response);

  if (!response.ok) {
    const message =
      (typeof payload.error === "string" && payload.error) ||
      (typeof payload.message === "string" && payload.message) ||
      `Request failed (${response.status})`;
    throw new ApiError(message, response.status, payload);
  }

  return payload as T;
}

// A 500 from the Hosting layer, a gateway timeout, or an empty 204 all arrive
// without parseable JSON. Returning {} lets the status decide the message
// rather than blowing up on a SyntaxError nobody can act on.
async function readJson(response: Response): Promise<Record<string, unknown>> {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return {};
  }
}

// ── Account ─────────────────────────────────────────────────────────────────

export const getMe = () => request<Me>("/me");

// The signed-in person's email has just been confirmed: their bots that send
// handoffs nowhere get the address (functions/index.js emailConfirmed).
export const emailConfirmed = () => request<{ ok: true; connected: number }>("/me/email-confirmed", { method: "POST", body: {} });

// Our own email with the link to confirm the account's address, or a corrected
// one (functions/lib/accountEmail.js).
export const sendConfirmationEmail = (address?: string) =>
  request<{ ok: true; sentTo: string }>("/me/confirmation-email", { method: "POST", body: address ? { address } : {} });

export const createTenant = (name: string) =>
  request<{ tenantId: string; name: string; role: string }>("/tenants", {
    method: "POST",
    body: { name }
  });

// ── Agents ──────────────────────────────────────────────────────────────────

export const listAgents = (tenantId: string) =>
  request<{ agents: DashboardAgent[] }>(`/tenants/${tenantId}/agents`);

export const getAgent = (tenantId: string, agentId: string, signal?: AbortSignal) =>
  request<{ agent: DashboardAgent }>(`/tenants/${tenantId}/agents/${agentId}`, { signal });

export const createAgent = (input: CreateAgentInput) =>
  request<{ agentId: string; publicId: string }>("/agents", { method: "POST", body: input });

export const updateAgent = (input: UpdateAgentInput) =>
  request<{ agent: DashboardAgent }>("/agents/update", { method: "POST", body: input });

export const retryProvisioning = (tenantId: string, agentId: string) =>
  request<{ ok: true }>("/agents/retry", { method: "POST", body: { tenantId, agentId } });

export const archiveAgent = (tenantId: string, agentId: string) =>
  request<{ ok: true }>("/agents/archive", { method: "POST", body: { tenantId, agentId } });

// Irreversible, and the server checks that for itself: it refuses anything that
// is not already archived, and anything still being billed for.
export const deleteAgentForever = (tenantId: string, agentId: string) =>
  request<{ ok: true }>("/agents/delete", { method: "POST", body: { tenantId, agentId } });

// ── Knowledge ───────────────────────────────────────────────────────────────

export const listKnowledge = (tenantId: string, agentId: string) =>
  request<{ sources: StoredKnowledgeSource[]; removed?: RemovedKnowledgeSource[] }>(`/tenants/${tenantId}/agents/${agentId}/knowledge`);

export const addKnowledge = (tenantId: string, agentId: string, sources: KnowledgeSource[]) =>
  request<{ ok: true; added: number }>("/agents/knowledge", {
    method: "POST",
    body: { tenantId, agentId, sources }
  });

// Moves the source to the recently removed list, restorable for 30 days.
export const deleteKnowledge = (tenantId: string, agentId: string, sourceId: string) =>
  request<{ ok: true; purgeAt?: string | null }>("/agents/knowledge/delete", {
    method: "POST",
    body: { tenantId, agentId, sourceId }
  });

export const restoreKnowledge = (tenantId: string, agentId: string, sourceId: string) =>
  request<{ ok: true }>("/agents/knowledge/restore", {
    method: "POST",
    body: { tenantId, agentId, sourceId }
  });

// Replaces every pair of one Q&A source; editing or removing a single question
// sends the group with that one changed.
export const updateKnowledgePairs = (tenantId: string, agentId: string, sourceId: string, pairs: QaPair[]) =>
  request<{ ok: true; pairs: QaPair[] }>("/agents/knowledge/pairs", {
    method: "POST",
    body: { tenantId, agentId, sourceId, pairs }
  });

const signUpload = (input: {
  tenantId: string;
  agentId?: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
}) => request<SignedUpload>("/uploads/sign", { method: "POST", body: input });

// Two steps, and the second one does not go through request(): the file is PUT
// straight to Cloud Storage on a signed URL, because routing a big file through a
// Cloud Function would cross the network twice and tie up an instance the chat
// proxy needs.
export async function uploadKnowledgeFile(
  tenantId: string,
  agentId: string | undefined,
  file: File
): Promise<{ storagePath: string; fileName: string; sizeBytes: number }> {
  const contentType = file.type || "application/octet-stream";
  const signed = await signUpload({
    tenantId,
    agentId,
    fileName: file.name,
    contentType,
    sizeBytes: file.size
  });

  const put = await fetch(signed.url, {
    method: "PUT",
    // The signed URL is bound to this exact content type; sending a different
    // one gets a SignatureDoesNotMatch from Google rather than a useful error.
    headers: signed.headers,
    body: file
  });
  if (!put.ok) {
    // Cloud Storage enforces the size limit itself (the signed
    // x-goog-content-length-range header) and answers 400 EntityTooLarge.
    const body = await put.text().catch(() => "");
    if (/EntityTooLarge/i.test(body)) {
      throw new ApiError(fileTooBigMessage({ fileName: file.name, ext: fileExtension(file.name), sizeBytes: file.size }), 400);
    }
    throw new ApiError(`Upload failed (${put.status}). Please try again.`, put.status);
  }

  return { storagePath: signed.storagePath, fileName: file.name, sizeBytes: file.size };
}

// ── Booking ─────────────────────────────────────────────────────────────────
//
// The credential goes up on connect and never comes back down: every response
// here is the published view, which has no secrets in it. That is enforced
// server-side -- these types only describe what arrives.

export const getBooking = (tenantId: string, agentId: string) =>
  request<{ booking: BookingStatus }>(`/tenants/${tenantId}/agents/${agentId}/booking`);

// The bookable things on the connected account, fetched live rather than
// stored: an event type the tenant deleted this morning should not still be in
// the picker.
export const listBookingTargets = (tenantId: string, agentId: string) =>
  request<{ targets: BookingTargetOption[] }>(`/tenants/${tenantId}/agents/${agentId}/booking/targets`);

export const connectBooking = (input: {
  tenantId: string;
  agentId: string;
  provider: string;
  apiKey?: string;
  accessToken?: string;
  url?: string;
}) => request<{ booking: BookingStatus }>("/agents/booking/connect", { method: "POST", body: input });

export const configureBooking = (input: {
  tenantId: string;
  agentId: string;
  target?: BookingTarget;
  settings?: Partial<BookingSettings>;
  meeting?: { title?: string; description?: string; location?: string };
}) => request<{ booking: BookingStatus }>("/agents/booking/configure", { method: "POST", body: input });

export const disconnectBooking = (tenantId: string, agentId: string) =>
  request<{ booking: BookingStatus }>("/agents/booking/disconnect", {
    method: "POST",
    body: { tenantId, agentId }
  });

// ── Team handoff ────────────────────────────────────────────────────────────

export const getHandoff = (tenantId: string, agentId: string) =>
  request<{ handoff: HandoffStatus }>(`/tenants/${tenantId}/agents/${agentId}/handoff`);

// `url` for Slack, Discord and webhooks, `address` for email. `label` is the
// tenant's own name for a URL channel.
export const connectHandoff = (input: {
  tenantId: string;
  agentId: string;
  kind: HandoffKind;
  url?: string;
  address?: string;
  label?: string;
}) => request<{ handoff: HandoffStatus }>("/agents/handoff/connect", { method: "POST", body: input });

// Name a connected channel, or clear the name with "".
export const labelHandoff = (tenantId: string, agentId: string, kind: HandoffKind, label: string) =>
  request<{ handoff: HandoffStatus }>("/agents/handoff/label", { method: "POST", body: { tenantId, agentId, kind, label } });

// Choose the bot's short link name (buildbotsnow.com/c/<slug>). The old name
// keeps working.
export const setAgentLink = (tenantId: string, agentId: string, slug: string) =>
  request<{ slug: string; url: string }>("/agents/link", { method: "POST", body: { tenantId, agentId, slug } });

// Followed from a confirmation email, by someone who may have no account.
export const confirmHandoffEmail = (token: string) =>
  request<{ confirmed: boolean; address: string }>("/handoff/email/confirm", {
    method: "POST",
    body: { token },
    authenticated: false
  });

export const disconnectHandoff = (tenantId: string, agentId: string, kind: HandoffKind) =>
  request<{ handoff: HandoffStatus }>("/agents/handoff/disconnect", { method: "POST", body: { tenantId, agentId, kind } });

export const testHandoff = (tenantId: string, agentId: string, kind: HandoffKind) =>
  request<{ handoff: HandoffStatus }>("/agents/handoff/test", { method: "POST", body: { tenantId, agentId, kind } });

// ── The tenant's own API ────────────────────────────────────────────────────

// A bot has up to three APIs (functions/lib/customApi.js API_SLOTS); every
// call below is about one of them, the first when none is named.
export type ApiSlot = "api" | "api2" | "api3";
export const API_SLOTS: ApiSlot[] = ["api", "api2", "api3"];

type ApiScope = { tenantId: string; agentId: string; slot?: ApiSlot };

const slotQuery = (slot?: ApiSlot) => (slot && slot !== "api" ? `?slot=${slot}` : "");

export const getCustomApi = (tenantId: string, agentId: string, slot?: ApiSlot) =>
  request<{ api: CustomApiStatus; slots?: Record<ApiSlot, boolean> }>(`/tenants/${tenantId}/agents/${agentId}/api${slotQuery(slot)}`);

// A link (specUrl) or the file's text (specText). Everything it lists arrives
// turned off; `added` and `removed` compare with what was there before.
export const connectCustomApi = (
  input: ApiScope & { specUrl?: string; specText?: string; auth: CustomApiAuthInput; baseUrl?: string }
) =>
  request<{ api: CustomApiStatus; added: string[]; removed: string[] }>("/agents/api/connect", {
    method: "POST",
    body: input
  });

export const configureCustomApi = (
  input: ApiScope & {
    operations: { key: string; enabled: boolean; description: string | null }[];
    baseUrl?: string;
    auth?: CustomApiAuthInput;
  }
) => request<{ api: CustomApiStatus }>("/agents/api/configure", { method: "POST", body: input });

export const refreshCustomApi = (tenantId: string, agentId: string, slot?: ApiSlot) =>
  request<{ api: CustomApiStatus; added: string[]; removed: string[] }>("/agents/api/refresh", {
    method: "POST",
    body: { tenantId, agentId, slot }
  });

export const testCustomApi = (tenantId: string, agentId: string, key: string, args: Record<string, string>, slot?: ApiSlot) =>
  request<{ result: CustomApiResult }>("/agents/api/test", { method: "POST", body: { tenantId, agentId, key, args, slot } });

export const disconnectCustomApi = (tenantId: string, agentId: string, slot?: ApiSlot) =>
  request<{ api: CustomApiStatus }>("/agents/api/disconnect", { method: "POST", body: { tenantId, agentId, slot } });

export const listApiCalls = (tenantId: string, agentId: string, slot?: ApiSlot) =>
  request<{ calls: CustomApiCall[] }>(`/tenants/${tenantId}/agents/${agentId}/api/calls${slotQuery(slot)}`);

// ── Transcripts ─────────────────────────────────────────────────────────────

// A page of conversations, newest first. 30 days is everything we keep, so it
// sends no `since` at all.
export function listSessions(
  tenantId: string,
  agentId: string,
  filters: SessionFilters = { since: "30d", unanswered: false, upset: false },
  cursor: string | null = null
) {
  const query = new URLSearchParams();
  if (filters.since !== "30d") query.set("since", filters.since);
  if (filters.unanswered) query.set("unanswered", "1");
  if (filters.upset) query.set("upset", "1");
  if (cursor) query.set("cursor", cursor);
  const suffix = query.toString() ? `?${query}` : "";
  // historyLimitedDays: a free bot's list only reaches back this far.
  return request<{ sessions: SessionRow[]; nextCursor: string | null; historyLimitedDays?: number | null }>(
    `/tenants/${tenantId}/agents/${agentId}/sessions${suffix}`
  );
}

export const listTurns = (tenantId: string, agentId: string, sessionId: string) =>
  request<Transcript>(`/tenants/${tenantId}/agents/${agentId}/sessions/${sessionId}/turns`);

// The conversation a handoff came from, for its "Take over this chat" link.
// sessionId is null while the handoff is not tied to one yet.
export const getHandoffConversation = (tenantId: string, agentId: string, handoffId: string) =>
  request<{ sessionId: string | null; session: SessionRow | null }>(
    `/tenants/${tenantId}/agents/${agentId}/handoffs/${encodeURIComponent(handoffId)}`
  );

// One conversation as a list row, to open it from the waiting-visitor alert.
export const getSession = (tenantId: string, agentId: string, sessionId: string) =>
  request<{ session: SessionRow }>(`/tenants/${tenantId}/agents/${agentId}/sessions/${encodeURIComponent(sessionId)}`);

// "Available for live chat": sent every 15 seconds while switched on, and once
// with false to switch off. Answers with who is available and who is waiting.
// `fresh` is switching it on (as opposed to a check-in), which also clears a
// pause left by a missed visitor.
export const setLiveAvailability = (tenantId: string, agentId: string, available: boolean, fresh = false) =>
  request<LiveAvailability>("/agents/live/availability", {
    method: "POST",
    body: { tenantId, agentId, available, ...(fresh ? { fresh: true } : {}) }
  });

// Live takeover. Each answers with the conversation as it now stands.
type LiveIds = { tenantId: string; agentId: string; sessionId: string };

export const takeOverConversation = (ids: LiveIds) =>
  request<Transcript>("/agents/conversations/takeover", { method: "POST", body: ids });

export const replyInConversation = (ids: LiveIds, text: string) =>
  request<Transcript>("/agents/conversations/reply", { method: "POST", body: { ...ids, text } });

export const handBackConversation = (ids: LiveIds) =>
  request<Transcript>("/agents/conversations/handback", { method: "POST", body: ids });

// ── Billing ─────────────────────────────────────────────────────────────────

export const getBilling = (tenantId: string) =>
  request<BillingSummary>(`/tenants/${tenantId}/billing`);

// Either a client secret and the publishable key, to show Stripe's checkout
// inside the dashboard (components/CheckoutDialog), or a URL to Stripe's own
// page when the server has no publishable key configured.
export const startAgentCheckout = (tenantId: string, agentId: string, plan: PaidPlanId = "starter") =>
  request<{ url?: string; clientSecret?: string; publishableKey?: string }>("/billing/checkout", {
    method: "POST",
    body: { tenantId, agentId, plan }
  });

// Stripe prorates; the plan shows once its webhook has landed, a moment later.
// "free" ends the subscription when the period already paid for does.
export const changeAgentPlan = (tenantId: string, agentId: string, plan: PaidPlanId | "free") =>
  request<{ ok: true; plan: PaidPlanId | "free"; endsAt?: string | null }>("/billing/plan", { method: "POST", body: { tenantId, agentId, plan } });

// Undo a pending move to Free.
export const keepAgentPlan = (tenantId: string, agentId: string) =>
  request<{ ok: true }>("/billing/keep", { method: "POST", body: { tenantId, agentId } });

// Whole dollars of extra answers a month, or null for no limit.
export const setSpendingCap = (tenantId: string, agentId: string, capUsd: number | null) =>
  request<{ agent: DashboardAgent }>("/agents/spending-cap", { method: "POST", body: { tenantId, agentId, capUsd } });

export const openBillingPortal = (tenantId: string) =>
  request<{ url: string }>("/billing/portal", { method: "POST", body: { tenantId } });

// Asks for an estimate; charges nothing. The rate is no longer a published
// number, so there is no total to compute here and no Stripe URL to follow.
export const requestDevTime = (tenantId: string, note: string, timeline: string) =>
  request<{ ok: true; requestId: string }>("/devtime/request", {
    method: "POST",
    body: { tenantId, note, timeline }
  });

// ── Team ────────────────────────────────────────────────────────────────────

export const getTeam = (tenantId: string) => request<TeamView>(`/tenants/${tenantId}/team`);

export const inviteToTeam = (tenantId: string, email: string, role: TeamMember["role"]) =>
  request<TeamView>("/team/invite", { method: "POST", body: { tenantId, email, role } });

export const revokeInvite = (tenantId: string, inviteId: string) =>
  request<TeamView>("/team/invite/revoke", { method: "POST", body: { tenantId, inviteId } });

export const setTeamRole = (tenantId: string, uid: string, role: TeamMember["role"]) =>
  request<TeamView>("/team/role", { method: "POST", body: { tenantId, uid, role } });

export const removeFromTeam = (tenantId: string, uid: string) =>
  request<TeamView>("/team/remove", { method: "POST", body: { tenantId, uid } });

export const leaveTeam = (tenantId: string) => request<{ ok: true }>("/team/leave", { method: "POST", body: { tenantId } });

export const setOwnAlerts = (tenantId: string, handoffAlerts: boolean) =>
  request<TeamView>("/team/alerts", { method: "POST", body: { tenantId, handoffAlerts } });

export const renameAccount = (tenantId: string, name: string) =>
  request<TeamView>("/team/name", { method: "POST", body: { tenantId, name } });

export const setBillingEmail = (tenantId: string, billingEmail: string | null) =>
  request<TeamView>("/team/billing-email", { method: "POST", body: { tenantId, billingEmail } });

export const acceptInvite = (token: string) =>
  request<{ tenantId: string; name: string; role: TeamMember["role"] }>("/invites/accept", { method: "POST", body: { token } });

// Our own view of every account (pages/Admin.tsx); 404 for anyone not on the
// admin list.
export const getAdminOverview = () => request<AdminOverview>("/admin/overview");
export const getAdmins = () => request<AdminList>("/admin/admins");
// { add: email } or { remove: email }; the owner only.
export const changeAdmins = (change: { add?: string; remove?: string }) =>
  request<AdminList>("/admin/admins", { method: "POST", body: change });
